import React, { useState } from 'react';
import { Box } from '@material-ui/core';
import * as R from 'ramda';
import { CustomDialog, NumberFormatCustom } from '../../../custom-forms';
import { TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { useTranslate } from '../../../hooks/useTranslate';

export interface CalculationPartAddDialogProps {
  open: boolean;
  initialPart: CaseCalculationPart;
  qualities: IReferential[];
  isAdd: boolean;
  onClose: () => void;
  onSave: (part: CaseCalculationPart) => void;
}

const CalculationPartAddDialog: React.FC<CalculationPartAddDialogProps> = ({
  open,
  initialPart,
  qualities,
  isAdd,
  onClose,
  onSave
}) => {
  const t = useTranslate();
  const [calculationPart, setCalculationPart] = useState<CaseCalculationPart>(initialPart);

  const handleName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCalculationPart((prev) => ({ ...prev, name }));
  };

  const handleGuideNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const guideNumber = e.target.value.trimLeft();
    setCalculationPart((prev) => ({ ...prev, guideNumber }));
  };

  const handlePriceChangeOriginalPart = (e: any) => {
    const value = e.target.value;
    setCalculationPart((prev) => ({
      ...prev,
      originalPrice: value === '' ? 0 : parseFloat(value)
    }));
  };

  const handleUnitsOriginalPart = (e: any) => {
    const value = e.target.value;
    setCalculationPart((prev) => ({ ...prev, pieceNr: value === '' ? 0 : parseFloat(value) }));
  };

  const handleQuality = (_e: React.SyntheticEvent, v: IReferential | null) =>
    setCalculationPart((prev) => ({
      ...prev,
      carPartCategory: v,
      carPartCategoryId: R.isNil(v) ? 0 : v.id
    }));

  const submit = () => {
    onSave(calculationPart);
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      title={
        isAdd
          ? t(
              'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_ADD_ORIGINAL_PART_LABEL',
              'Adaugă piesa originală'
            )
          : t(
              'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_COPY_ORIGINAL_PART_LABEL',
              'Copiază piesa originală'
            )
      }
      cancelLabel={t('ALT_CALC_CANCEL', 'Renunță')}
      confirmLabel={t('SAVE', 'Salveaza')}
    >
      <Box mb={2}>
        <TextValidator
          variant="outlined"
          fullWidth
          id="name"
          name="name"
          label={t('CALCULATION_DETAILS_DESCRIPTION_HEADER', 'Descriere')}
          value={calculationPart.name}
          onChange={handleName}
          validators={['required']}
          errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
        />
      </Box>

      <Box mb={2}>
        <TextValidator
          variant="outlined"
          fullWidth
          id="guideNumber"
          name="guideNumber"
          label={t('CALCULATION_DETAILS_GUIDE_NUMBER_HEADER', 'Cod')}
          value={calculationPart.guideNumber}
          onChange={handleGuideNumber}
          validators={['required']}
          errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
        />
      </Box>

      <Box mb={2}>
        <TextValidator
          variant="outlined"
          fullWidth
          disabled={!isAdd}
          id="originalPrice"
          name="originalPrice"
          label={t('CALCULATION_DETAILS_UNIT_PRICE_HEADER', 'Pret Unitar')}
          value={
            isAdd
              ? calculationPart.originalPrice || ''
              : (
                  Math.round(
                    calculationPart.originalPrice * (1 - calculationPart.discount / 100) * 100
                  ) / 100
                ).toFixed(2)
          }
          onChange={handlePriceChangeOriginalPart}
          InputProps={{ inputComponent: NumberFormatCustom }}
          validators={['required', 'minNumber:1']}
          errorMessages={[
            t('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
            t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
          ]}
        />
      </Box>

      <Box mb={2}>
        <Autocomplete
          fullWidth
          options={qualities}
          value={calculationPart.carPartCategory ?? null}
          getOptionLabel={(o: IReferential) => o.code ?? ''}
          onChange={handleQuality}
          renderInput={(params) => (
            <TextValidator
              {...params}
              variant="outlined"
              fullWidth
              name="quality"
              value={calculationPart.carPartCategory}
              label={t('CALCULATION_DETAILS_QUALITY_HEADER', 'Calitate')}
              validators={['required']}
              errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
            />
          )}
        />
      </Box>

      <Box mb={2}>
        <TextValidator
          variant="outlined"
          fullWidth
          id="pieceNr"
          name="pieceNr"
          InputProps={{ inputComponent: NumberFormatCustom }}
          label={t('CALCULATION_DETAILS_UNITS_HEADER', 'Unitati')}
          value={calculationPart.pieceNr || ''}
          onChange={handleUnitsOriginalPart}
          validators={['required', 'minNumber:1']}
          errorMessages={[
            t('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
            t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
          ]}
        />
      </Box>
    </CustomDialog>
  );
};

export default CalculationPartAddDialog;
